import ReportQ from "./ReportQ";
import SdkInfo from "./SdkInfo";
import Token from "./Token";
import BleHandler from "./BLEHandler";

class SaapApi {
  static instance = null;
  static withAuth;

  static getInstance() {
    if (this.instance === null) {
      this.instance = new SaapApi();
    }
    return this.instance;
  }

  // Application context
  context = null;
  sdkInfo = null;
  bleHandler = null;

  // Application types
  static APP_FACTORY_CONFIG = 1; // Factory provisioning application
  static APP_SMART_LOCKER_SHARE = 10; // Smart lock for shared access
  static APP_SMART_LOCKER_STORAGE = 11; // Locker terminal for personnel storage
  static APP_SMART_BOX_OWN = 12; // Personnel individual storage
  static APP_VEHICLE_OWN = 13; // Personnel vehicle access
  static APP_FLEET_VEHICLE_SHARE = 14; // Shared vehicle access
  static APP_FLEET_DRIVER_SHARE = 15; // Shared vehicle access
  static APP_SMART_LOCK_OWN = 16; // Smart lock for private access
  static APP_SMART_LOCK_SHARE = 17; // Locker terminal with shared access

  // Token types
  static TOKEN_CONFIG_ROOT = 1; // root token
  static TOKEN_CONFIG_PBK = 3; // backup public key token
  static TOKEN_TRANS_SINGLE_USE_TIME_BOUNDED = 4; // token is for single use and works within timebound
  static TOKEN_TRANS_REPEAT_USE_TIME_BOUNDED = 5; // token is for multiple use and works within timebound
  static TOKEN_TRANS_SINGLE_USE_TIME_UNBOUNDED = 6; // token is for single use but not time bounded
  static TOKEN_TRANS_PERIODIC_USE_TIME_BOUNDED = 7; // token is for periodic use

  static TOKEN_CONFIG_TAG = 8; // config tag
  static TOKEN_REMOVE_TAG = 9; // remove tag

  static TOKEN_SERVICE = 100;

  // Actions
  static LOCKER_ACTION_NONE = 0;
  static LOCKER_ACTION_UNLOCK = 1;
  static LOCKER_ACTION_LOCK = 2;
  static LOCKER_ACTION_LOCK_STATUS = 3;
  static LOCKER_ACTION_LOCK_STATUS_ALL = 4;
  static LOCKER_ACTION_SERVICE_UNLOCK = 5;
  static LOCKER_ACTION_SERVICE_UNLOCK_ALL = 6;
  static LOCKER_ACTION_BATT_STATUS = 7;
  static LOCKER_ACTION_DISCONNECT_DEVICE = 8;
  static LOCKER_ACTION_TEMP_UNLOCK = 9;
  static LOCKER_ACTION_UNLOCK_WITH_PIN = 10;
  static LOCKER_ACTION_SET_PIN = 11;
  static LOCKER_ACTION_GET_PIN = 12;
  static LOCKER_ACTION_SET_MASTER_PIN = 13;

  // Response codes
  static RESP_OK = "OK";
  static RESP_ERROR = "ERROR";

  // Notifications to app
  static NOTIF_NONE = "NOTIF_NONE";
  static NOTIF_BLUETOOTH_ERROR = "NOTIF_BLUETOOTH_ERROR";
  static NOTIF_TOKEN_ERROR = "NOTIF_TOKEN_ERROR";
  static NOTIF_LOCK_LOCKED = "NOTIF_LOCK_LOCKED";
  static NOTIF_LOCK_UNLOCKED = "NOTIF_LOCK_UNLOCKED";
  static NOTIF_UNLOCK_ERROR = "NOTIF_UNLOCK_ERROR";
  static NOTIF_BOX_ERROR = "NOTIF_BOX_ERROR";
  static NOTIF_LOW_BATT = "NOTIF_LOW_BATT";
  static NOTIF_RETRY = "NOTIF_RETRY";
  static NOTIF_CONFIG = "NOTIF_CONFIG";
  static NOTIF_DEBUG = "NOTIF_DEBUG";
  static NOTIF_TRANS_COMPLETED = "NOTIF_TRANS_COMPLETED";
  static NOTIF_SRV_REPORT = "NOTIF_SRV_REPORT";
  static NOTIF_OTHER_ERROR = "NOTIF_OTHER_ERROR";
  static NOTIF_BLUETOOTH_BEGIN = "NOTIF_BLUETOOTH_BEGIN";
  static NOTIF_BLUETOOTH_END = "NOTIF_BLUETOOTH_END";

  // Api Response
  static Response = class {
    constructor(code, text, id, key, devId) {
      this.code = code; // message code
      this.text = text; // message text formatted as json
      this.sdkId = id;
      this.pKey = key;
      this.deviceId = devId;
    }

    dump() {
      console.log("Response: " + this.code + " > " + this.text);
    }
  };

  constructor() {
    const rep = new ReportQ({
      onEmpty: () => {
        console.log("onReportQ: Empty");
      },
    });
    rep.add("HELLO!");
    rep.end();
  }

  // Interface to notify token processing status
  setListener(listener) {
    this.listener = listener;
  }

  async init(ctx) {
    if (this.context !== null) {
      return new SaapApi.Response(
        SaapApi.RESP_OK,
        "",
        this.sdkInfo.sdkId,
        this.sdkInfo.sdkKey,
        this.sdkInfo.deviceId
      );
    }
    try {
      this.context = ctx;
      this.sdkInfo = new SdkInfo(ctx);

      return new SaapApi.Response(
        SaapApi.RESP_OK,
        "",
        this.sdkInfo.sdkId,
        this.sdkInfo.sdkKey,
        this.sdkInfo.deviceId
      );
    } catch (e) {
      console.log("init: SAAP-ERROR...................");
      return new SaapApi.Response(SaapApi.RESP_ERROR, "", "", "", "");
    }
  }

  async config(strToken) {
    let ble;
    let token = null;

    // Parse token
    try {
      token = new Token(strToken, await this.sdkInfo.getPrivateKey());
      token.dump();
    } catch (e) {
      if (this.listener)
        this.listener.onNotification(
          SaapApi.NOTIF_TOKEN_ERROR,
          "sdk: Invalid Token",
          ""
        );
      return SaapApi.RESP_ERROR;
    }

    const btAddr = token.btAddr;

    // Send token to device
    try {
      ble = BleHandler.getInstance(false);
      ble.processToken(
        this.context,
        0,
        token,
        false,
        SaapApi.LOCKER_ACTION_UNLOCK,
        this.sdkInfo,
        {
          onNotification: (code, message) => {
            if (this.listener)
              this.listener.onNotification(code, message, btAddr);
          },
        }
      );
    } catch (e) {
      console.log("Error! processCmd: " + e.toString());
      if (this.listener)
        this.listener.onNotification(
          SaapApi.NOTIF_BLUETOOTH_ERROR,
          "Ble error",
          ""
        );
      return SaapApi.RESP_ERROR;
    }
    return SaapApi.RESP_OK;
  }

  executeEx(assetId, strToken, action, authType, authToken) {
    return this.executeToken(assetId, strToken, action);
  }

  async executeToken(assetId, strToken, action, pin = null) {
    let ble;
    let token = null;

    this.listener.onNotification(
      SaapApi.NOTIF_BLUETOOTH_BEGIN,
      "Ble begin",
      ""
    );

    // Parse token
    try {
      if (strToken && strToken.length > 0) {
        token = new Token(strToken, await this.sdkInfo.getPrivateKey());
        token.dump();
        console.log("TOKEN DUMPED");
      }
    } catch (e) {
      if (this.listener)
        this.listener.onNotification(
          SaapApi.NOTIF_TOKEN_ERROR,
          "Invalid Token",
          ""
        );
      return SaapApi.RESP_ERROR;
    }

    const btAddr = token.btAddr;

    // Send token to device
    try {
      ble = BleHandler.getInstance(false);

      await ble.processToken(
        this.context,
        assetId,
        token,
        false,
        action,
        this.sdkInfo,
        {
          onNotification: (code, message) => {
            if (this.listener)
              this.listener.onNotification(code, message, btAddr);
          },
        },
        pin
      );
    } catch (e) {
      console.log("Error! processCmd: " + e.toString());
      if (this.listener)
        this.listener.onNotification(
          SaapApi.NOTIF_BLUETOOTH_ERROR,
          "Ble error",
          ""
        );
      return SaapApi.RESP_ERROR;
    }
    return SaapApi.RESP_OK;
  }

  async executeCommand(action, pin = null) {
    this.listener.onNotification(
      SaapApi.NOTIF_BLUETOOTH_BEGIN,
      "Ble begin",
      ""
    );

    // Send command to device
    try {
      let ble = BleHandler.getInstance(false);

      await ble.processCommand(
        this.context,
        action,
        this.sdkInfo,
        {
          onNotification: (code, message) => {
            if (this.listener) this.listener.onNotification(code, message, "");
          },
        },
        pin
      );
    } catch (e) {
      console.log("Error! processCmd: " + e.toString());
      if (this.listener)
        this.listener.onNotification(
          SaapApi.NOTIF_BLUETOOTH_ERROR,
          "Ble error",
          ""
        );
      return SaapApi.RESP_ERROR;
    }
    return SaapApi.RESP_OK;
  }

  executeCommandX(action, btAddr) {
    let ble;

    this.listener.onNotification(
      SaapApi.NOTIF_BLUETOOTH_BEGIN,
      "Ble begin",
      ""
    );

    // Send action
    try {
      ble = BleHandler.getInstance(false);
      ble.processCommand(this.context, btAddr, action, this.sdkInfo, {
        onNotification: (code, message) => {
          if (this.listener)
            this.listener.onNotification(code, message, btAddr);
        },
      });
    } catch (e) {
      console.log("Error! processCommand: " + e.toString());
      if (this.listener)
        this.listener.onNotification(
          SaapApi.NOTIF_BLUETOOTH_ERROR,
          "Ble error",
          ""
        );
      return SaapApi.RESP_ERROR;
    }
    return SaapApi.RESP_OK;
  }
}

export default SaapApi;
